import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { RefreshCw, Trash2, CalendarDays, Lock, LogOut } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ember-admin-token";

const STATUS_COLORS = {
  pending: "text-[#e05e00] border-[#ff6b00]/40 bg-[#ff6b00]/10",
  confirmed: "text-emerald-600 border-emerald-500/40 bg-emerald-500/10",
  completed: "text-neutral-500 border-neutral-300 bg-neutral-100",
  cancelled: "text-red-500 border-red-400/40 bg-red-400/10",
};

const formatApiErrorDetail = (detail) => {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(detail);
};

const PasscodeGate = ({ onUnlock }) => {
  const { t } = useLang();
  const A = t.admin;
  const [passcode, setPasscode] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!passcode) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { passcode });
      sessionStorage.setItem(TOKEN_KEY, res.data.token);
      onUnlock(res.data.token);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || A.wrong);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="admin-gate" className="min-h-screen flex items-center justify-center px-5 bg-[#faf9f6]">
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-sm rounded-2xl border border-[#e8e5e0] bg-white shadow-xl shadow-neutral-200/60 p-10 text-center"
      >
        <img src="/logos/emberline-final-512.png" alt="Emberline" className="w-20 h-20 mx-auto object-contain" />
        <h1 className="mt-6 font-display text-2xl text-neutral-900">{A.lockTitle}</h1>
        <p className="mt-2 text-sm text-neutral-500">{A.lockSub}</p>
        <div className="mt-8 relative">
          <Lock size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input
            data-testid="admin-passcode-input"
            type="password"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            placeholder={A.passPlaceholder}
            className="w-full rounded-full border border-neutral-300 bg-[#faf9f6] pl-11 pr-5 py-3.5 text-sm text-neutral-900 focus:border-[#ff6b00] focus:outline-none transition-colors"
          />
        </div>
        <button
          data-testid="admin-passcode-submit"
          type="submit"
          disabled={loading}
          className="btn-fire mt-5 w-full rounded-full py-3.5 font-tech text-xs font-semibold tracking-widest uppercase text-white disabled:opacity-60"
        >
          {loading ? "..." : A.enter}
        </button>
      </motion.form>
    </main>
  );
};

export default function Admin() {
  const { t } = useLang();
  const A = t.admin;
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const logout = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setAppointments([]);
  }, []);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/appointments`, { headers: authHeaders() });
      setAppointments(res.data);
    } catch (err) {
      if (err.response?.status === 401) logout();
      else toast.error("Error loading appointments");
    } finally {
      setLoading(false);
    }
  }, [token, authHeaders, logout]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/appointments/${id}`, { status }, { headers: authHeaders() });
      setAppointments((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
    } catch (err) {
      if (err.response?.status === 401) logout();
      else toast.error("Error updating status");
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/appointments/${id}`, { headers: authHeaders() });
      setAppointments((prev) => prev.filter((a) => a.id !== id));
      toast.success("Deleted");
    } catch (err) {
      if (err.response?.status === 401) logout();
      else toast.error("Error deleting");
    }
  };

  if (!token) return <PasscodeGate onUnlock={setToken} />;

  return (
    <main data-testid="admin-page" className="pt-28 pb-24 min-h-screen">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-tech text-xs tracking-[0.35em] uppercase text-neutral-400 mb-3">— Admin</p>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium text-neutral-900">{A.title}</h1>
            <p className="mt-2 text-sm text-neutral-500">{A.sub}</p>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="admin-refresh-btn" onClick={load} className="btn-ghost-silver rounded-full px-5 py-2.5 font-tech text-xs tracking-widest uppercase text-neutral-700 flex items-center gap-2">
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> {A.refresh}
            </button>
            <button data-testid="admin-logout-btn" onClick={logout} className="rounded-full px-5 py-2.5 font-tech text-xs tracking-widest uppercase text-neutral-400 border border-neutral-300 hover:text-red-500 hover:border-red-300 transition-colors flex items-center gap-2">
              <LogOut size={13} /> {A.logout}
            </button>
          </div>
        </div>

        <div className="mt-10 rounded-xl border border-[#e8e5e0] bg-white shadow-sm overflow-x-auto">
          {appointments.length === 0 && !loading ? (
            <div data-testid="admin-empty-state" className="p-16 text-center text-neutral-400">
              <CalendarDays size={40} className="mx-auto mb-4 opacity-40" />
              <p className="font-tech text-sm tracking-widest uppercase">{A.empty}</p>
            </div>
          ) : (
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-[#e8e5e0] bg-[#faf9f6]">
                  {[A.cols.client, A.cols.contact, A.cols.service, A.cols.when, A.cols.status, ""].map((c, i) => (
                    <th key={i} className="px-5 py-4 font-tech text-[11px] tracking-[0.2em] uppercase text-neutral-400">{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody data-testid="admin-appointments-table">
                {appointments.map((a) => (
                  <tr key={a.id} data-testid={`admin-row-${a.id}`} className="border-b border-[#f0ede8] hover:bg-[#faf9f6] transition-colors">
                    <td className="px-5 py-4 text-neutral-900 font-medium">{a.name}</td>
                    <td className="px-5 py-4 text-neutral-500">
                      <p>{a.phone}</p>
                      <p className="text-xs text-neutral-400">{a.email}</p>
                    </td>
                    <td className="px-5 py-4 text-neutral-600">{a.service}</td>
                    <td className="px-5 py-4">
                      <p className="text-[#e05e00] font-tech">{a.date}</p>
                      <p className="text-xs text-neutral-400">{a.time_slot}</p>
                    </td>
                    <td className="px-5 py-4">
                      <select
                        data-testid={`admin-status-select-${a.id}`}
                        value={a.status}
                        onChange={(e) => updateStatus(a.id, e.target.value)}
                        className={`rounded-full border px-3 py-1.5 text-xs font-tech tracking-wider bg-transparent focus:outline-none ${STATUS_COLORS[a.status]}`}
                      >
                        {Object.entries(A.statuses).map(([k, v]) => (
                          <option key={k} value={k} className="bg-white text-neutral-800">{v}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-5 py-4">
                      <button data-testid={`admin-delete-btn-${a.id}`} onClick={() => remove(a.id)} className="p-2 rounded-full text-neutral-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </main>
  );
}
